import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { formatPct } from "@/lib/api";

/**
 * KpiTile — the primary dashboard summary card.
 *
 * Issue #1 — supports `linkTo` to make a tile clickable. When provided,
 * the card renders as a React Router <Link/> and gets a hover ring + an
 * arrow icon to signal interactivity.
 *
 * Issue #15 — `value` uses a responsive clamp() so long values (e.g.
 * "126 / 143" or "Rs. 8.37T") shrink to fit without truncation.
 */
const KpiTile = ({
  label,
  value,
  delta,
  deltaSuffix = "",
  helpText,
  icon: Icon,
  testId,
  accent = false,
  linkTo = null,
  // Tailwind classes for the primary number. Default uses clamp() so it
  // auto-shrinks if the content is long.
  valueClassName = "text-[clamp(1.125rem,2.2vw,1.75rem)] leading-tight",
}) => {
  const trend = typeof delta === "number" ? (delta > 0 ? "up" : delta < 0 ? "down" : "flat") : null;
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendColor =
    trend === "up"
      ? "text-[hsl(var(--success))]"
      : trend === "down"
        ? "text-[hsl(var(--destructive))]"
        : "text-muted-foreground";

  const clickable = !!linkTo;

  const body = (
    <Card
      data-testid={testId || "dashboard-kpi-tile"}
      className={`p-5 border bg-card transition-colors duration-150 ${
        clickable
          ? "cursor-pointer hover:border-primary/50 hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-primary/40"
          : "hover:border-[hsl(var(--ring))]/40"
      } ${accent ? "bg-accent/40" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
          {label}
        </span>
        {clickable ? (
          <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
        ) : (
          Icon && <Icon className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div
        className={`mt-3 ${valueClassName} font-display font-semibold tabular-nums whitespace-nowrap overflow-hidden text-ellipsis`}
        title={typeof value === "string" ? value : undefined}
        data-testid={`${testId || "dashboard-kpi"}-value`}
      >
        {value}
      </div>
      {(trend || helpText) && (
        <div className="mt-2 flex items-center gap-2 text-xs" data-testid="dashboard-kpi-delta">
          {trend && (
            <span className={`flex items-center gap-1 ${trendColor}`}>
              <TrendIcon className="h-3 w-3" />
              {formatPct(delta)}
              {deltaSuffix}
            </span>
          )}
          {helpText && <span className="text-muted-foreground">{helpText}</span>}
        </div>
      )}
    </Card>
  );

  const wrapper = (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={clickable ? "group" : ""}
    >
      {body}
    </motion.div>
  );

  if (clickable) {
    return (
      <Link
        to={linkTo}
        className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        data-testid={`${testId || "dashboard-kpi"}-link`}
        aria-label={typeof value === "string" ? `${label}: ${value}` : label}
      >
        {wrapper}
      </Link>
    );
  }
  return wrapper;
};

export default KpiTile;
